import {
    Box,
    Button,
    Paper,
    type PaperProps,
    TablePagination,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    type DataGridProProps,
    type GridColumnVisibilityModel,
    GridFooterContainer,
    GridPreferencePanelsValue,
    useGridApiContext,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { type FC, type ReactNode } from 'react'
import { RosterFiltersDrawer } from 'recruiter/components/RosterTableToolbar'
import {
    SearchResultsWithPaginationTable,
    type SearchResultsWithPaginationTableProps,
} from 'recruiter/components/SearchResultsWithPaginationTable'
import { useRosterSqlSearch } from 'recruiter/hooks'
import { ColumnsManagementWithShowAll } from 'shared/components/ColumnsManagementWithShowAll'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { type RosterSortField } from 'shared/hooks/rosterSqlSearchUrlState'
import { getPxAsRem } from 'shared/utils'
import {
    type RosterTableColumnDefinition,
    type RosterTableRowModel,
} from './rosterTableColumns'

export type { RosterTableRowModel }

interface RosterSearchResultsTableProps
    extends Partial<
        SearchResultsWithPaginationTableProps<RosterTableRowModel>
    > {
    storageKey: string
    columns: RosterTableColumnDefinition[]
    toolbar: ReactNode
    containerProps?: PaperProps
    checkboxSelection?: boolean
    getDetailPanelContent?: DataGridProProps<RosterTableRowModel>['getDetailPanelContent']
    getDetailPanelHeight?: DataGridProProps<RosterTableRowModel>['getDetailPanelHeight']
}

const RosterSqlTableFooter: FC = () => {
    const apiRef = useGridApiContext()
    const { tableState, setTableState, totalItems } = useRosterSqlSearch()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length

    return (
        <GridFooterContainer>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <TablePagination
                component="div"
                count={totalItems}
                page={tableState.page - 1}
                rowsPerPage={tableState.limit}
                onPageChange={(_, newPage) => {
                    if (totalItems > 0) {
                        setTableState({ page: newPage + 1 })
                    }
                }}
                onRowsPerPageChange={(e) =>
                    setTableState({
                        limit: parseInt(e.target.value, 10),
                        page: 1,
                    })
                }
                rowsPerPageOptions={[25, 50, 100]}
            />
        </GridFooterContainer>
    )
}

export function RosterSearchResultsTable({
    storageKey,
    columns,
    toolbar,
    containerProps,
    checkboxSelection = false,
    getDetailPanelContent,
    getDetailPanelHeight,
    ...props
}: RosterSearchResultsTableProps) {
    const {
        data,
        isLoading,
        tableState,
        setTableState,
        selectedRosterIds,
        setSelectedRosterIds,
        isRowSelectable,
    } = useRosterSqlSearch()
    const gridApi = useGridApiRef()
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useLocalStorage<GridColumnVisibilityModel>(storageKey, {})
    const [columnWidths, setColumnWidths] = useLocalStorage<
        Record<string, number>
    >(`${storageKey}-column-widths`, {})

    const rows = (data ?? []) as RosterTableRowModel[]
    const columnsWithWidths = columns.map((col) => {
        const storedWidth = columnWidths[col.field]
        return storedWidth != null
            ? { ...col, width: storedWidth, flex: undefined }
            : col
    })

    const sortModel = tableState.sort
        .map((s) => {
            const col = columns.find((c) => c.sortField === s.field)
            return col
                ? { field: col.field, sort: s.order as 'asc' | 'desc' }
                : null
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)

    const handleSortModelChange: DataGridProProps['onSortModelChange'] = (
        sortModel
    ) => {
        if (sortModel.length === 0) {
            setTableState({ sort: [], page: 1 })
            return
        }

        const sortFields = sortModel
            .map((item) => {
                const col = columns.find(
                    (c) => c.field === item.field && c.sortField
                )
                if (!col?.sortField || !item.sort) return null
                return {
                    field: col.sortField as RosterSortField,
                    order: item.sort as 'asc' | 'desc',
                }
            })
            .filter(
                (field): field is NonNullable<typeof field> => field !== null
            )

        if (sortFields.length === 0) return

        setTableState({ sort: sortFields, page: 1 })
    }

    return (
        <Paper
            elevation={0}
            {...containerProps}
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingX: getPxAsRem(20),
                paddingTop: getPxAsRem(16),
                ...containerProps?.sx,
            }}
        >
            {toolbar}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    overflow: 'hidden',
                }}
            >
                <RosterFiltersDrawer />
                <SearchResultsWithPaginationTable
                    apiRef={gridApi}
                    columns={columnsWithWidths}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    onColumnWidthChange={(params) => {
                        setColumnWidths((prev) => ({
                            ...prev,
                            [params.colDef.field]: params.width,
                        }))
                    }}
                    disableMultipleColumnsSorting
                    keepNonExistentRowsSelected
                    loading={isLoading}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    rows={rows}
                    checkboxSelection={checkboxSelection}
                    isRowSelectable={isRowSelectable}
                    rowSelectionModel={selectedRosterIds}
                    onRowSelectionModelChange={(newSelection) =>
                        setSelectedRosterIds(newSelection as string[])
                    }
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    sortingMode="server"
                    pagination={false}
                    slots={{
                        toolbar: () => null,
                        footer: RosterSqlTableFooter,
                        columnsManagement: ColumnsManagementWithShowAll,
                        ...(getDetailPanelContent && {
                            detailPanelExpandIcon: ChevronRightIcon,
                            detailPanelCollapseIcon: ExpandMoreIcon,
                        }),
                    }}
                    slotProps={{
                        panel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementHeader': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnsManagement': {
                                    padding: 1,
                                },
                                '& .MuiDataGrid-columnsManagementFooter': {
                                    paddingLeft: 1,
                                    '& > label': {
                                        marginLeft: 0,
                                    },
                                },
                            },
                        },
                        columnsPanel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementRow': {
                                    py: 0.25,
                                    marginLeft: 0,
                                },
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.75rem',
                                },
                                '& .MuiCheckbox-root': {
                                    transform: 'scale(0.8)',
                                },
                                '& .MuiButton-root': {
                                    fontSize: '0.6875rem',
                                    textTransform: 'none',
                                    py: 0.5,
                                    px: 1,
                                    minWidth: 'auto',
                                },
                            },
                        },
                    }}
                    rowHeight={41}
                    columnHeaderHeight={56}
                    sx={{
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            borderTop: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        },
                        '& .MuiDataGrid-cell[data-colindex="0"]:not(data-field="__detail_panel_toggle__"), & .MuiDataGrid-columnHeader[aria-colindex="1"]':
                            {
                                pl: 2,
                            },
                        '& .MuiDataGrid-filler > div': {
                            borderTop: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            pl: 2,
                        },
                        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root':
                            {
                                transform: 'scale(0.8)',
                            },
                        '& .MuiDataGrid-cell--detailPanelToggleCell': {
                            justifyContent: 'center',
                        },
                    }}
                    {...props}
                />
            </Box>
        </Paper>
    )
}
